import apiFunctions from '..';
import { executeFilter } from '../kirbyFiltering';
import { failure, tooManyRequests, unauthorized } from '../../types/api';
import { ALL_HORIZON_REGIONS } from '../../types/horizonOptOutTracker';
import type {
  HorizonConfig,
  HorizonIngestApp,
  HorizonOptOutDetails,
  HorizonRegionFilter,
} from '../../types/horizonOptOutTracker';
import { KirbyFilterDto, KirbyFilterResultPage } from '../../types/kirbyFiltering';
import {
  createHorizonOptOutDetails,
  normalizeHorizonEventLog,
  normalizeHorizonIngestApp,
  resolveHorizonConfigs,
} from '../../utility/horizonOptOutTracker';

const pageSize = 100;

const clause = (Property: string, Operator: string, Value: string): KirbyFilterDto => ({
  Clause: { Property, Operator, Value },
});

const and = (...Children: KirbyFilterDto[]): KirbyFilterDto => ({ Conjunction: 'And', Children });

const assertConfigResponse = (response: unknown): unknown => {
  if (response === failure || response === unauthorized || response === tooManyRequests) {
    throw new Error('The Horizon configurations could not be loaded.');
  }
  return response;
};

const loadAllFilterPages = async (
  filterType: 'IngestApp' | 'EventLog',
  Filter: KirbyFilterDto,
  signal?: AbortSignal,
) => {
  const records: Record<string, unknown>[] = [];
  let pageNumber = 1;
  let totalCount = 0;

  do {
    const request = {
      Filter,
      PageNumber: pageNumber,
      PageSize: pageSize,
    };
    const page: KirbyFilterResultPage = signal
      ? await executeFilter(filterType, request, signal)
      : await executeFilter(filterType, request);
    records.push(...page.results);
    totalCount = page.totalCount;
    if (page.results.length === 0 && records.length < totalCount) {
      throw new Error(`${filterType} pagination ended before all records were returned.`);
    }
    pageNumber += 1;
  } while (records.length < totalCount);

  return records;
};

const loadIngestApps = async (
  config: HorizonConfig,
  windowStart: string,
  windowEnd: string,
): Promise<HorizonIngestApp[]> => {
  const records = await loadAllFilterPages(
    'IngestApp',
    and(
      clause('OnDemandConfigId', 'Equals', String(config.id)),
      clause('IsDisqualified', 'Equals', 'true'),
      clause('CreatedUTC', 'GreaterThanOrEqual', windowStart),
      clause('CreatedUTC', 'LessThanOrEqual', windowEnd),
    ),
  );

  return records.flatMap((record) => {
    const ingestApp = normalizeHorizonIngestApp(record, config);
    return ingestApp ? [ingestApp] : [];
  });
};

/** Loads opt-out details for one disqualified Horizon application within the selected date range. */
export const getHorizonOptOutDetails = async (
  ingestApp: HorizonIngestApp,
  windowStart: string,
  windowEnd: string,
  signal?: AbortSignal,
): Promise<HorizonOptOutDetails | null> => {
  const records = await loadAllFilterPages(
    'EventLog',
    and(
      clause('AppUid', 'Equals', ingestApp.appUid),
      clause('EventDateUTC', 'GreaterThanOrEqual', windowStart),
      clause('EventDateUTC', 'LessThanOrEqual', windowEnd),
    ),
    signal,
  );
  const windowStartTimestamp = Date.parse(windowStart);
  const windowEndTimestamp = Date.parse(windowEnd);
  const events = records.flatMap((record) => {
    const event = normalizeHorizonEventLog(record);
    const eventTimestamp = event ? Date.parse(event.eventDateUTC) : Number.NaN;
    return event?.appUid === ingestApp.appUid &&
      eventTimestamp >= windowStartTimestamp &&
      eventTimestamp <= windowEndTimestamp
      ? [event]
      : [];
  });

  return createHorizonOptOutDetails(events);
};

/**
 * Loads disqualified IngestApps created within the selected range.
 *
 * A region without a configuration in this environment contributes no applications, so the grid
 * stays usable for the regions that are configured.
 */
export const getHorizonIngestApps = async (
  windowStart: string,
  windowEnd: string,
  region: HorizonRegionFilter = ALL_HORIZON_REGIONS,
): Promise<HorizonIngestApp[]> => {
  const configResponse = assertConfigResponse(await apiFunctions.apiPost('Kirby/ConfigDetails/GetAllConfigs'));
  const configs = resolveHorizonConfigs(configResponse).filter(
    (config) => region === ALL_HORIZON_REGIONS || config.region === region,
  );
  const ingestAppGroups = await Promise.all(configs.map((config) => loadIngestApps(config, windowStart, windowEnd)));
  return [...new Map(ingestAppGroups.flat().map((ingestApp) => [ingestApp.appUid, ingestApp])).values()];
};
